/**
 * Compact Pagination for Media Picker Modal
 *
 * A smaller, more compact pagination component specifically for the Select Media modal.
 * - Page size options are multiples of grid columns (4)
 * - Smaller text and buttons
 * - No "Showing x to y of z" display
 */

import { cn } from "@/lib/utils";

interface CompactPaginationProps {
  numItems: number;
  currentPage: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onChange: (page: number, pageSize: number) => void;
  className?: string;
}

export function CompactPagination({
  numItems,
  currentPage,
  pageSize,
  pageSizeOptions = [12, 24, 48, 96],
  onChange,
  className = "",
}: CompactPaginationProps) {
  const numPages = Math.ceil(numItems / pageSize);

  const onPageChange = (newPage: number) => {
    if (newPage < 1 || newPage > numPages || currentPage === newPage) {
      return;
    }
    onChange(newPage, pageSize);
  };

  const onPageSizeChange = (newPageSize: number) => {
    onChange(1, newPageSize);
  };

  // Generate page numbers to display (simple version: show first 3 pages or current page)
  const pages: (number | { placeholder: true })[] = [];

  if (numPages <= 5) {
    // Show all pages if 5 or fewer
    for (let i = 1; i <= numPages; i++) {
      pages.push(i);
    }
  } else {
    // Show first page, current page area, and last page with ellipsis
    pages.push(1);

    if (currentPage > 3) {
      pages.push({ placeholder: true });
    }

    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(numPages - 1, currentPage + 1);
      i++
    ) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    if (currentPage < numPages - 2) {
      pages.push({ placeholder: true });
    }

    if (!pages.includes(numPages)) {
      pages.push(numPages);
    }
  }

  return (
    <div
      className={cn(
        "relative flex items-center justify-between pt-2 pb-2",
        className
      )}
    >
      {/* Page size selector - floated left */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-neutral-600">Per page</span>
        <div className="inline-flex rounded border border-neutral-200 p-0.5 bg-neutral-50">
          {pageSizeOptions.map((size) => {
            const isActive = pageSize === size;
            return (
              <button
                type="button"
                key={`page-size-${size}`}
                onClick={() => onPageSizeChange(size)}
                className={cn(
                  "h-6 min-w-[32px] px-1.5 rounded transition-all duration-150",
                  "flex items-center justify-center",
                  "text-xs font-medium leading-none",
                  isActive
                    ? "bg-white text-neutral-900 shadow-sm"
                    : "text-neutral-600 hover:text-neutral-900"
                )}
              >
                {size}
              </button>
            );
          })}
        </div>
      </div>

      {/* Page selector - centered */}
      <div className="flex items-center gap-0.5 absolute left-1/2 -translate-x-1/2">
        {/* Previous button */}
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={cn(
            "h-6 w-6 min-w-[24px] rounded transition-all duration-150",
            "flex items-center justify-center",
            "text-xs leading-none",
            "border border-neutral-200 bg-white hover:bg-neutral-50",
            "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
          )}
          aria-label="Previous"
        >
          <i className="fa-solid fa-chevron-left text-[10px] text-neutral-500" />
        </button>

        {/* Page numbers */}
        {pages.map((page, index) => {
          if (typeof page === "object" && "placeholder" in page) {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-1 text-neutral-400 text-xs"
              >
                ...
              </span>
            );
          }

          const pageNum = page as number;
          const isActive = currentPage === pageNum;

          return (
            <button
              key={`page-${pageNum}`}
              type="button"
              onClick={() => onPageChange(pageNum)}
              className={cn(
                "h-6 w-6 min-w-[24px] rounded transition-all duration-150",
                "flex items-center justify-center",
                "text-xs font-medium leading-none",
                isActive
                  ? "bg-blue-500 text-white border border-blue-500 shadow-sm hover:bg-blue-600 active:bg-blue-700"
                  : "text-neutral-700 hover:bg-neutral-50"
              )}
            >
              {pageNum}
            </button>
          );
        })}

        {/* Next button */}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === numPages}
          className={cn(
            "h-6 w-6 min-w-[24px] rounded transition-all duration-150",
            "flex items-center justify-center",
            "text-xs leading-none",
            "border border-neutral-200 bg-white hover:bg-neutral-50",
            "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
          )}
          aria-label="Next"
        >
          <i className="fa-solid fa-chevron-right text-[10px] text-neutral-500" />
        </button>
      </div>
    </div>
  );
}
